/**
 * `<OrgMembersRoute>` — `/w/:slug/organization/members`
 *
 * The account-tier analogue of `<SettingsMembersRoute>`: lists everyone with an
 * `OrgMembership` in the caller's organization and lets an org admin change
 * their org role (Org Admin / Billing Admin / Member). Data is org-scoped
 * (`/api/org/*`), not workspace-scoped — the route lives under `/w/:slug` only
 * to reuse the authenticated settings shell + the `useAbility()` provider.
 *
 * Gating mirrors the workspace tier: the role cell is an editable `<Select>`
 * only when `ability.can("update-role", "OrgMember")` and the row isn't the
 * acting user's own (R4 mirrored client-side; the server still enforces it).
 */

import { authClient, useAbility } from "@cometchat-ai/auth/client";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  TableBody,
  TableHeader,
} from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { Building2 } from "../components/icons";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { PageShell } from "../components/page-shell";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { OrgRoleSelect } from "../components/org-role-select";
import { RolePill, StatusPill, accentForKey } from "../components/settings-parts";
import { dicebearMicah } from "../lib/avatar";

function initials(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

export function OrgMembersRoute() {
  const { t } = useTranslation("workspace");
  const ability = useAbility();

  const membersQuery = useQuery({
    queryKey: ["org-members"],
    queryFn: () => authClient.listOrgMembers(),
  });

  const canChangeRole = ability.can("update-role", "OrgMember");
  const members = membersQuery.data ?? [];
  const memberCount = members.length;

  const countLabel = useMemo(
    () =>
      memberCount === 1
        ? t("organization.members.countOne")
        : t("organization.members.countOther", { count: memberCount }),
    [memberCount, t],
  );

  return (
    <PageShell
      data-testid="org-members"
      className="p-0"
      title={t("organization.members.title")}
      description={t("organization.members.subtitle")}
    >
      {membersQuery.isPending ? (
        <ListSkeleton
          rows={5}
          itemClassName="h-10 w-full"
          className="space-y-2 p-6"
          data-testid="org-members-skeleton"
        />
      ) : membersQuery.error ? (
        <div className="px-6 py-12">
          <ErrorState message={membersQuery.error.message} onRetry={membersQuery.refetch} />
        </div>
      ) : members.length === 0 ? (
        <div className="px-6 py-12">
          <EmptyState
            data-testid="org-members-empty"
            icon={Building2}
            title={t("organization.members.title")}
            description={t("organization.members.empty")}
          />
        </div>
      ) : (
        <>
          <div className="flex items-center px-6 py-2.5">
            <span className="text-[13px] text-muted-foreground" data-testid="org-members-count">
              {countLabel}
            </span>
          </div>
          <DataGrid data-testid="org-members-table">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6 pr-4">
                  {t("organization.members.table.user")}
                </DataGridHead>
                <DataGridHead className="w-44">{t("organization.members.table.role")}</DataGridHead>
                <DataGridHead className="w-32">
                  {t("organization.members.table.status")}
                </DataGridHead>
                <DataGridHead className="w-36 pl-4 pr-6" divider={false}>
                  {t("organization.members.table.joined")}
                </DataGridHead>
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const accent = accentForKey(member.email);
                const seed =
                  [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email;
                return (
                  <DataGridRow key={member.id} data-testid={`org-member-row-${member.id}`}>
                    <DataGridCell className="pl-6 pr-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8 shrink-0 rounded-lg">
                          <AvatarImage src={dicebearMicah(seed)} alt="" className="rounded-lg" />
                          <AvatarFallback
                            className="rounded-lg text-xs font-medium"
                            style={{
                              background: `color-mix(in srgb, ${accent} 14%, transparent)`,
                              color: accent,
                            }}
                          >
                            {initials(member.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          {member.firstName || member.lastName ? (
                            <div className="truncate text-[13px] font-medium text-foreground">
                              {member.firstName} {member.lastName}
                            </div>
                          ) : null}
                          <div className="truncate text-[13px] text-muted-foreground">
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </DataGridCell>
                    <DataGridCell>
                      {canChangeRole && !member.isSelf ? (
                        <OrgRoleSelect memberId={member.id} currentRole={member.role} />
                      ) : (
                        <RolePill
                          role={member.role}
                          label={t(`organization.members.roles.${member.role}` as const)}
                        />
                      )}
                    </DataGridCell>
                    <DataGridCell>
                      <StatusPill
                        active={member.status === "active"}
                        label={member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                      />
                    </DataGridCell>
                    <DataGridCell className="whitespace-nowrap pl-4 pr-6" divider={false}>
                      {formatDate(member.joinedAt)}
                    </DataGridCell>
                  </DataGridRow>
                );
              })}
            </TableBody>
          </DataGrid>
        </>
      )}
    </PageShell>
  );
}
